// ONE facility filter for the production area (surface review F11, 2026-08-12).
//
// Four sibling screens asked the same question — "which plant?" — with two
// different controls:
//
//   * S1 (work orders) and S4 (dispatch) used the §3.10 `Field` grammar
//     (`label.filter-chip` + an 11 px uppercase caption) with an "Any facility"
//     option, URL-backed;
//   * S5 (shifts) and S6 (calibration) hand-rolled a bare `<label>` whose
//     uppercase styling was declared twice in `production.css`, with NO
//     all-facilities option and a silent default to the first facility in the
//     list.
//
// So the same filter sat at two heights, carried two captions and answered
// "all plants" on two screens and never on the other two. This component is
// the single control. The one legitimate difference between the pairs stays a
// PROP, not a second implementation: a board that reports one facility's day
// (shifts, calibration drift) cannot answer "all facilities", so it renders
// without the all-option and resolves a default; a list that can span them
// offers it.
//
// The caption is the tenant's own word for a facility (`useTerm`), which is
// what S1 and S4 already showed — S5/S6's hardcoded "Facility" ignored the
// terminology the tenant chose in onboarding.
//
// The `data-testid` rides the SELECT, because that is what the certified
// module-04 walkthroughs drive (`getByTestId("shifts-facility")
// .selectOption(id)` in six specs, `calibration-facility` in two).

import { useTranslation } from "react-i18next";
import { Field } from "../../../../components/field";
import { useTerm } from "../../../onboarding/hooks/terminology";

export interface FacilityOption {
  readonly id: string;
  readonly name: string;
}

export interface FacilityFilterProps {
  /** The selected facility id — "" when the caller allows "all facilities". */
  value: string;
  onChange: (facilityId: string) => void;
  facilities: readonly FacilityOption[];
  /** Rides the `<select>`, where the walkthroughs read it. */
  testId?: string | undefined;
  /**
   * Offer the "all facilities" option. False for a screen whose answer is
   * per-facility by construction (a shift board, a drift chart).
   */
  allowAll?: boolean;
}

export function FacilityFilter({
  value,
  onChange,
  facilities,
  testId,
  allowAll = false,
}: FacilityFilterProps) {
  const { t } = useTranslation();
  const { term } = useTerm();
  return (
    <Field label={term("facility")}>
      <select
        value={value}
        data-testid={testId}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {allowAll ? <option value="">{t("production.list.filter.anyFacility")}</option> : null}
        {facilities.map((facility) => (
          <option key={facility.id} value={facility.id}>
            {facility.name}
          </option>
        ))}
      </select>
    </Field>
  );
}
